import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Col } from 'react-bootstrap';

import FORMATTERS from 'helpers/formatters';
import {
  FormCurrencyField,
  FormDateField,
  FormSelectField,
  FormTextField,
  Button,
} from '_components/_core';
import { ContactForm } from '_components/_shared';
import CustomFormSelectField from '_components/Transactions/components/CustomFormSelectField/CustomFormSelectField';

function ChargeDetails({ values, setFieldValue, paymentGateways, recipients }) {
  const isEditMode = values.id;
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState([]);

  const recurrenceCycleOptions = useMemo(
    () => [
      { value: 'WEEKLY', label: 'Semanal' },
      { value: 'BIWEEKLY', label: 'Quinzenal' },
      { value: 'MONTHLY', label: 'Mensal' },
      { value: 'BIMONTHLY', label: 'Bimestral' },
      { value: 'QUARTERLY', label: 'Trimestral' },
      { value: 'SEMIANNUALLY', label: 'Semestral' },
      { value: 'YEARLY', label: 'Anual' },
    ],
    [],
  );

  const paymentGatewayOptions = useMemo(
    () =>
      (paymentGateways || [])
        .filter(gateway => gateway && gateway.id)
        .map(gateway => ({
          value: gateway.id,
          label:
            gateway.payment_gateway_name || gateway.mnemonic || `Gateway ${gateway.id}`,
        })),
    [paymentGateways],
  );

  const formattedRecipients = useMemo(() => {
    return recipients.map(recipient => ({
      value: recipient.id,
      label: recipient.name,
    }));
  }, [recipients]);

  const getInstallmentsOptions = useCallback(amount => {
    return Array.from({ length: 24 }, (_, index) => index + 1).map(number => ({
      value: number,
      label:
        number === 1
          ? `À vista (${FORMATTERS.NUMBER(amount)})`
          : `${number} parcelas de ${FORMATTERS.NUMBER(amount / number)}`,
    }));
  }, []);

  const handleProviderChange = useCallback(
    ({ value }) => {
      const selectedGateway = paymentGateways.find(gateway => gateway.id === value);

      const payment_methods = selectedGateway.payment_methods.map(method => ({
        value: method.id,
        label: method.name,
      }));

      setPaymentMethodOptions(payment_methods);

      setFieldValue('company_payment_gateway_id', value);
      setFieldValue('payment_method_payment_gateway_id', null);
    },
    [paymentGateways, setFieldValue],
  );

  const handleIsRecurringChange = useCallback(
    ({ value }) => {
      setFieldValue('is_recurring', value);

      if (!value) {
        setFieldValue('recurrence_cycle', null);
        setFieldValue('charge_type', 'SINGLE');
      }

      if (value) {
        setFieldValue('total_installments', 1);
        setFieldValue('charge_type', 'RECURRING');
      }
    },
    [setFieldValue],
  );

  return (
    <>
      <ContactForm
        isOpen={isContactFormOpen}
        contact_id={values.recipient_id}
        onToggleForm={() => setIsContactFormOpen(!isContactFormOpen)}
        onAfterSaveCallback={() => setIsContactFormOpen(false)}
      />
      <Form className="p-3">
        <Form.Row>
          <Form.Group as={Col} lg={12}>
            <Form.Label>Criar cobrança com</Form.Label>
            <FormSelectField
              name="company_payment_gateway_id"
              options={paymentGatewayOptions}
              placeholder="Selecione um provedor de pagamentos"
              onChange={handleProviderChange}
              disabled={!!values.id}
            />
          </Form.Group>
        </Form.Row>
        {values.company_payment_gateway_id && (
          <>
            <h4>Dados da Cobrança</h4>
            <Form.Row className="mt-4">
              <Form.Group as={Col} lg={6}>
                <Form.Label>Contato / Cliente</Form.Label>
                <CustomFormSelectField
                  name="recipient_id"
                  options={formattedRecipients}
                  creatable="recipient"
                  metadata={{
                    type: null,
                  }}
                  loadingMessage={() => 'Carregando...'}
                  onCreateCallback={created_recipient => {
                    if (created_recipient) {
                      setFieldValue('recipient_id', created_recipient.id);
                    }
                  }}
                  width="100%"
                  placeholder="Selecione o contato"
                  isDisabled={isEditMode}
                />

                <Button
                  variant="link"
                  size="sm"
                  className="m-0 p-0"
                  disabled={!values.recipient_id || isEditMode}
                  onClick={() => setIsContactFormOpen(true)}
                  tabIndex={-1}
                >
                  Editar cliente
                </Button>
              </Form.Group>
              <Form.Group as={Col} lg={6}>
                <Form.Label>Forma de pagamento</Form.Label>
                <FormSelectField
                  name="payment_method_payment_gateway_id"
                  options={paymentMethodOptions}
                  placeholder="Selecione a forma de pagamento"
                  disabled={isEditMode}
                />
              </Form.Group>
            </Form.Row>
            <Form.Row>
              <Form.Group as={Col} lg={6}>
                <Form.Label>Valor da cobrança</Form.Label>
                <FormCurrencyField name="amount" placeholder="0,00" />
              </Form.Group>
              <Form.Group as={Col} lg={2}>
                <Form.Label>Recorrente?</Form.Label>
                <FormSelectField
                  name="is_recurring"
                  options={[
                    { value: false, label: 'Não' },
                    { value: true, label: 'Sim' },
                  ]}
                  placeholder="Selecione..."
                  onChange={handleIsRecurringChange}
                  disabled={isEditMode}
                />
              </Form.Group>
              {values.is_recurring && (
                <Form.Group as={Col} lg={4}>
                  <Form.Label>Frequência</Form.Label>
                  <FormSelectField
                    name="recurrence_cycle"
                    options={recurrenceCycleOptions}
                    placeholder="Selecione a frequência"
                    disabled={isEditMode}
                  />
                </Form.Group>
              )}
              {!values.is_recurring && (
                <Form.Group as={Col} lg={4}>
                  <Form.Label>Parcelas</Form.Label>
                  <CustomFormSelectField
                    name="total_installments"
                    options={getInstallmentsOptions(values.amount)}
                    onChange={({ value }) => {
                      setFieldValue('total_installments', value);
                      if (value > 1) {
                        setFieldValue('charge_type', 'INSTALLMENT');
                      } else {
                        setFieldValue('charge_type', 'SINGLE');
                      }
                    }}
                    loadingMessage={() => 'Carregando...'}
                    width="100%"
                    placeholder="Selecione o número de parcelas"
                    disabled={!values.amount || values.is_recurring || isEditMode}
                  />
                </Form.Group>
              )}
            </Form.Row>
            <Form.Row>
              <Form.Group as={Col} lg={4}>
                <Form.Label>
                  {values.total_installments > 1 || values.is_recurring
                    ? 'Vencimento da primeira cobrança'
                    : 'Vencimento da cobrança'}
                </Form.Label>
                <FormDateField name="due_date" placeholder="dd/mm/aaaa" />
              </Form.Group>
              <Form.Group as={Col} lg={8}>
                <Form.Label>Descrição</Form.Label>
                <FormTextField name="description" placeholder="Descrição da cobrança" />
                <small className="text-muted d-block mt-2">
                  A descrição informada será impressa no boleto.
                </small>
              </Form.Group>
            </Form.Row>
            <hr />
          </>
        )}
      </Form>
    </>
  );
}

ChargeDetails.defaultProps = {
  paymentMethods: [],
  paymentGateways: [],
  recipients: [],
};

ChargeDetails.propTypes = {
  values: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  paymentMethods: PropTypes.array,
  paymentGateways: PropTypes.array,
  recipients: PropTypes.array,
};

export default ChargeDetails;
